import React, { Suspense } from 'react';
import axios from 'axios';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Analytics } from './components/Analytics';
import { AuthProvider } from './context/AuthContext';
import { API_URL } from './config/api';
import { ThemeProvider } from './context/ThemeContext';
import { ProtectedRoute, AdminRoute, ProTierRoute } from './components/ProtectedRoute';
import { MoeButton } from './components/MoeButton';
import { HomePage } from './pages/HomePage';
import { DashboardPage } from './pages/DashboardPage';
import { AdminPage } from './pages/AdminPage';
import { AdminFoundersPage } from './pages/AdminFoundersPage';
import { PGRDepartmentPage } from './pages/PGRDepartmentPage';
import { WeedControlPage } from './pages/WeedControlPage';
import { PestManagementPage } from './pages/PestManagementPage';
import { DiseaseManagementPage } from './pages/DiseaseManagementPage';
import { PartnersPage } from './pages/PartnersPage';
import { DisclosurePage } from './pages/DisclosurePage';
import { PropertyPage } from './pages/PropertyPage';
import { EquipmentPage } from './pages/EquipmentPage';
import { SeasonalCalendarPage } from './pages/SeasonalCalendarPage';
import { SpreaderSettingsPage } from './pages/SpreaderSettingsPage';
import { SoilAnalysisPage } from './pages/SoilAnalysisPage';
import SoilTestPage from './pages/SoilTestPage';
import ResistancePage from './pages/ResistancePage';
import NfcRedirectPage from './pages/NfcRedirectPage';
import HeyPage from './pages/HeyPage';
import { IrrigationPage } from './pages/IrrigationPage';
import { SprayForecastPage } from './pages/SprayForecastPage';
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage';
import { TermsOfServicePage } from './pages/TermsOfServicePage';
import { AccessibilityPage } from './pages/AccessibilityPage';
import { EmailPreferencesPage } from './pages/EmailPreferencesPage';
import { ProfilePage } from './pages/ProfilePage';
import { CockpitClassPage } from './pages/CockpitClassPage';
import { CockpitClassLessonPage } from './pages/CockpitClassLessonPage';
import { AdminCockpitClassPage } from './pages/AdminCockpitClassPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { MagicLoginPage } from './pages/MagicLoginPage';
import { AdminBackdoorPage } from './pages/AdminBackdoorPage';
import { WelcomeHubPage } from './pages/WelcomeHubPage';
import { SeasonReportPage } from './pages/SeasonReportPage';
import { ContractorSheetPage } from './pages/ContractorSheetPage';
import { SharedPropertyPage } from './pages/SharedPropertyPage';
import { LearnPage } from './pages/LearnPage';
import { FieldGuidePage } from './pages/FieldGuidePage';
import { MixCalculatorPage } from './pages/MixCalculatorPage';
import { AboutPage } from './pages/AboutPage';
import FallPrepGuidePage from './pages/FallPrepGuidePage';
import { SubscriptionSuccessPage } from './pages/SubscriptionSuccessPage';
import { MerchPage } from './pages/MerchPage';
import ShopRedirect from './pages/ShopRedirect';
import { MarketplacePage } from './pages/MarketplacePage';
import { MarketplaceTermsPage } from './pages/MarketplaceTermsPage';
import MyPlanPage from './pages/MyPlanPage';
import { InspectionsPage } from './pages/InspectionsPage';
import { VendorAssetsPage } from './pages/VendorAssetsPage';
import TryDiagnosisPage from './pages/TryDiagnosisPage';
import ProLandingPage from './pages/ProLandingPage';
import ClientsPage from './pages/ClientsPage';
import { PropertyMeasurePage } from './pages/PropertyMeasurePage';
import KitPage from './pages/KitPage';
import BYOSPage from './pages/BYOSPage';
import WaterBudgetPage from './pages/WaterBudgetPage';
import CatchCupPage from './pages/CatchCupPage';
import RachioConnectPage from './pages/RachioConnectPage';
import RebateHelperPage from './pages/RebateHelperPage';
import InstallChecklistPage from './pages/InstallChecklistPage';
import FungicideCheatSheetPage from './pages/FungicideCheatSheetPage';
import BlogIndexPage from './pages/BlogIndexPage';
import BlogPostPage from './pages/BlogPostPage';
import { BlogAdminListPage, BlogAdminEditorPage } from './pages/BlogAdminPages';
import BlogCalendarPage from './pages/BlogCalendarPage';
import AdminExportsPage from './pages/AdminExportsPage';
import TurfPulseDashboard from './pages/TurfPulseDashboard';
import MoisturePage from './pages/pulse/MoisturePage';
import ECPage from './pages/pulse/ECPage';
import VPDPage from './pages/pulse/VPDPage';
import MowWindowPage from './pages/pulse/MowWindowPage';
import ApplicationWindowsPage from './pages/pulse/ApplicationWindowsPage';
import TrendsPage from './pages/pulse/TrendsPage';
import ActivityPage from './pages/pulse/ActivityPage';
import RecentFiresPage from './pages/RecentFiresPage';
import MoistureAlertRulesPage from './pages/pulse/MoistureAlertRulesPage';
import CalibrationReferencePage from './pages/pulse/CalibrationReferencePage';
import AlertsPage from './pages/AlertsPage';
import AdminDecisionEnginePage from './pages/AdminDecisionEnginePage';
import ScorecardPage from './pages/ScorecardPage';
import SheetsExportDemo from './pages/SheetsExportDemo';
import { AdminCampaignsPage } from './pages/AdminCampaignsPage';
import { AdminAssetsPage } from './pages/AdminAssetsPage';
import { AdminShipLogPage } from './pages/AdminShipLogPage';
import AdminNurturePage from './pages/AdminNurturePage';
import SprayTodayPage from './pages/SprayTodayPage';
import ConsolePage from './pages/ConsolePage';
import WidgetPage from './pages/WidgetPage';
import SundayComparePage from './pages/SundayComparePage';
import HomeAssistantIntegrationPage from './pages/HomeAssistantIntegrationPage';
import InnerCircleSuccessPage from './pages/InnerCircleSuccessPage';
import SensorPlacementPage from './pages/SensorPlacementPage';
import SoilCalibrationWizard from './pages/SoilCalibrationWizard';
import ZoneProfilesPage from './pages/ZoneProfilesPage';
import AnthemPage from './pages/AnthemPage';
import SongbookPage from './pages/SongbookPage';
import AdminGlowUpPage from './pages/AdminGlowUpPage';
import ProbeDrivenIrrigationPage from './pages/ProbeDrivenIrrigationPage';
import DataSourcesPage from './pages/DataSourcesPage';
import PricingPage from './pages/PricingPage';
import GettingStartedPage from './pages/GettingStartedPage';
import EcowittToRachioPage from './pages/EcowittToRachioPage';
import HelpMeConnectEcowittPage from './pages/HelpMeConnectEcowittPage';
import AdminJournalPage from './pages/AdminJournalPage';
import ContractorsPage from './pages/ContractorsPage';
import RachioCalibrationVsProbesPage from './pages/RachioCalibrationVsProbesPage';
import ProtectYourProbesPage from './pages/ProtectYourProbesPage';
import RoundRockRebatePage from './pages/RoundRockRebatePage';
import RegionalLawnCarePage from './pages/RegionalLawnCarePage';
import ContractorPartnerPage from './pages/ContractorPartnerPage';
import { SiteFooter } from './components/SiteFooter';
import { CookieConsentBanner } from './components/CookieConsentBanner';
import { SkipNavigation } from './components/SkipNavigation';
import { AnnouncementBanner } from './components/AnnouncementBanner';
import { UpdateBanner } from './components/UpdateBanner';
import { GuideNudge } from './components/GuideNudge';
import { usePageTracking } from './hooks/usePageTracking';
import { Toaster } from './components/ui/sonner';
import './i18n'; // Initialize i18n
import './App.css';

// Loading fallback for i18n suspense
const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

// Shows WelcomeHub for logged-in users, HomePage (landing) for visitors.
// Also consumes any pending "post-auth intent" stashed in sessionStorage
// by the tier CTAs (Inner Circle, Homeowner Seasonal, Enthusiast Annual) —
// see HomePage.js, InnerCircleCTA.jsx, and PricingPage.jsx. This wrapper
// is the right place to handle it because HomePage unmounts the moment
// auth completes (user becomes truthy), so any logic inside HomePage's
// useEffect is racing the unmount. HomeOrHub stays mounted across the
// auth-state flip.
//
// Intent → Stripe endpoint mapping:
//   - 'inner-circle'        → POST /api/payments/inner-circle/checkout
//   - 'homeowner-seasonal'  → POST /api/payments/checkout {package_id: 'homeowner_seasonal'}
//   - 'enthusiast-annual'   → POST /api/payments/checkout {package_id: 'enthusiast_annual'}
const TIER_INTENT_MAP = {
  'inner-circle': {
    endpoint: '/api/payments/inner-circle/checkout',
    body: {},
    urlField: 'checkout_url',
  },
  'homeowner-seasonal': {
    endpoint: '/api/payments/checkout',
    body: { package_id: 'homeowner_seasonal' },
    urlField: 'url',
  },
  'enthusiast-annual': {
    endpoint: '/api/payments/checkout',
    body: { package_id: 'enthusiast_annual' },
    urlField: 'url',
  },
};

const HomeOrHub = () => {
  const { user, loading } = require('./context/AuthContext').useAuth();
  const [redirecting, setRedirecting] = React.useState(false);

  React.useEffect(() => {
    if (loading || !user || user === false) return;
    let intent = null;
    try { intent = sessionStorage.getItem('turfpulse_post_auth_intent'); } catch (e) { /* private mode */ }
    const cfg = intent && TIER_INTENT_MAP[intent];
    if (!cfg) return;
    // One-shot: clear immediately so a refresh doesn't re-fire.
    try { sessionStorage.removeItem('turfpulse_post_auth_intent'); } catch (e) { /* noop */ }
    setRedirecting(true);
    axios.post(`${API_URL}${cfg.endpoint}`, cfg.body, { withCredentials: true })
      .then((res) => {
        const url = res.data?.[cfg.urlField];
        if (url) {
          window.location.href = url;
        } else {
          setRedirecting(false); // fall back to WelcomeHub
        }
      })
      .catch((e) => {
        // Soft fail — let the user land on WelcomeHub and retry from there.
        // Admins and existing premium users will hit a 400 here (already in
        // the tier) — that's expected and harmless.
        console.warn(`Post-auth ${intent} checkout failed:`, e?.response?.data || e.message);
        setRedirecting(false);
      });
  }, [user, loading]);

  if (loading || redirecting) return <LoadingFallback />;
  // Logged-in default landing → /pulse (the live dashboard). The marketing
  // HomePage stays the front door for anonymous visitors. Users who want
  // the WelcomeHub onboarding can still reach it via /welcome.
  if (user) return <Navigate to="/pulse" replace />;
  return <HomePage />;
};

// Mounted inside BrowserRouter + AuthProvider so it can use router + auth context.
const AnalyticsMount = () => {
  usePageTracking();
  return null;
};

// Kiosk chrome: on wall-mounted dashboard routes + Pulse detail screens,
// hide the floating Moe bubble, update banner, and guide nudge so the
// dark dashboard stays edge-to-edge with no UI breaking the glance-from-
// 6-feet-away intent. The detail pages (/pulse/*) also need this — the
// "Top Tier" mockup explicitly shows no floating bubbles.
const KIOSK_PATHS = ['/console', '/widget', '/trends', '/activity'];
const KIOSK_PREFIXES = ['/pulse'];
const KioskAwareChrome = () => {
  const { useLocation } = require('react-router-dom');
  const location = useLocation();
  if (KIOSK_PATHS.includes(location.pathname)) return null;
  if (KIOSK_PREFIXES.some(p => location.pathname.startsWith(p + '/') || location.pathname === p)) return null;
  return (
    <>
      <MoeButton />
      <UpdateBanner />
      <GuideNudge />
    </>
  );
};

function App() {
  return (
    <Suspense fallback={<LoadingFallback />}>
    <ThemeProvider>
    <AuthProvider>
      <BrowserRouter>
        <KioskAwareChrome />
        <AnalyticsMount />
        <Analytics />
        <AnnouncementBanner />
        <SkipNavigation />
        <Routes>
          <Route path="/" element={<HomeOrHub />} />
          <Route
            path="/app"
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <AdminRoute>
                <AdminPage />
              </AdminRoute>
            }
          />
          <Route
            path="/pgr"
            element={
              <ProtectedRoute>
                <PGRDepartmentPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/weed"
            element={
              <ProtectedRoute>
                <WeedControlPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/pest"
            element={
              <ProtectedRoute>
                <PestManagementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/disease"
            element={
              <ProtectedRoute>
                <DiseaseManagementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/partners"
            element={<PartnersPage />}
          />
          <Route
            path="/disclosure"
            element={<DisclosurePage />}
          />
          <Route
            path="/shared/:shareToken"
            element={<SharedPropertyPage />}
          />
          <Route
            path="/property"
            element={
              <ProtectedRoute>
                <PropertyPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/equipment"
            element={
              <ProtectedRoute>
                <EquipmentPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/calendar"
            element={<SeasonalCalendarPage />}
          />
          <Route
            path="/calculator"
            element={
              <ProtectedRoute>
                <MixCalculatorPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/spreader-settings"
            element={<SpreaderSettingsPage />}
          />
          <Route
            path="/soil-analysis"
            element={
              <ProtectedRoute>
                <SoilAnalysisPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/soil-test"
            element={
              <ProtectedRoute>
                <SoilTestPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/resistance"
            element={
              <ProtectedRoute>
                <ResistancePage />
              </ProtectedRoute>
            }
          />
          <Route path="/nfc" element={<NfcRedirectPage />} />
          <Route path="/hey" element={<HeyPage />} />
          <Route path="/shop" element={<ShopRedirect />} />
          <Route
            path="/irrigation"
            element={
              <ProtectedRoute>
                <IrrigationPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/spray-forecast"
            element={
              <ProtectedRoute>
                <SprayForecastPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/privacy"
            element={<PrivacyPolicyPage />}
          />
          <Route
            path="/terms"
            element={<TermsOfServicePage />}
          />
          <Route
            path="/accessibility"
            element={<AccessibilityPage />}
          />
          <Route
            path="/email-preferences"
            element={
              <ProtectedRoute>
                <EmailPreferencesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            }
          />
          {/* Super Cockpit Class — public archive + auth'd detail + admin editor */}
          <Route path="/cockpit/class" element={<CockpitClassPage />} />
          <Route path="/cockpit/class/:slug" element={<CockpitClassLessonPage />} />
          <Route
            path="/admin/cockpit-class"
            element={
              <AdminRoute>
                <AdminCockpitClassPage />
              </AdminRoute>
            }
          />
          <Route
            path="/season-report"
            element={
              <ProtectedRoute>
                <SeasonReportPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/field-guide"
            element={
              <ProtectedRoute>
                <FieldGuidePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/contractor-sheet"
            element={
              <ProtectedRoute>
                <ContractorSheetPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reset-password"
            element={<ResetPasswordPage />}
          />
          <Route
            path="/magic-login"
            element={<MagicLoginPage />}
          />
          <Route
            path="/admin-portal"
            element={<AdminBackdoorPage />}
          />
          <Route
            path="/learn"
            element={
              <ProtectedRoute>
                <LearnPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/about"
            element={<AboutPage />}
          />
          <Route
            path="/about/the-anthem"
            element={<AnthemPage />}
          />
          <Route
            path="/about/the-songbook"
            element={<SongbookPage />}
          />
          <Route
            path="/songbook"
            element={<SongbookPage />}
          />
          <Route
            path="/fall-guide"
            element={<FallPrepGuidePage />}
          />
          <Route
            path="/payment-success"
            element={
              <ProtectedRoute>
                <PaymentSuccessPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/subscription-success"
            element={
              <ProtectedRoute>
                <SubscriptionSuccessPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/merch"
            element={
              <ProtectedRoute>
                <MerchPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/marketplace"
            element={
              <ProtectedRoute>
                <MarketplacePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/marketplace-terms"
            element={<MarketplaceTermsPage />}
          />
          <Route
            path="/my-plan"
            element={
              <ProtectedRoute>
                <MyPlanPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/inspections"
            element={
              <ProtectedRoute>
                <InspectionsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/vendor-assets"
            element={
              <ProtectedRoute>
                <VendorAssetsPage />
              </ProtectedRoute>
            }
          />
          {/* Property Measure — Pro / Business tier only. Backend enforces gating on /api/measurements/*. */}
          <Route
            path="/measure"
            element={
              <ProTierRoute>
                <PropertyMeasurePage />
              </ProTierRoute>
            }
          />
          <Route
            path="/clients"
            element={
              <ProTierRoute>
                <ClientsPage />
              </ProTierRoute>
            }
          />
          <Route path="/pro" element={<ProLandingPage />} />
          <Route path="/kit" element={<KitPage />} />
          <Route path="/byos" element={<BYOSPage />} />
          <Route path="/water-budget" element={<WaterBudgetPage />} />
          <Route path="/catch-cup" element={<CatchCupPage />} />
          <Route path="/rachio/connect" element={<RachioConnectPage />} />
          <Route path="/rachio/rebate-helper" element={<RebateHelperPage />} />
          <Route path="/rachio/install-checklist" element={<InstallChecklistPage />} />
          <Route path="/fungicide-cheat-sheet" element={<FungicideCheatSheetPage />} />
          <Route path="/blog" element={<BlogIndexPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/admin/blog" element={<BlogAdminListPage />} />
          <Route path="/admin/blog/calendar" element={<BlogCalendarPage />} />
          <Route path="/admin/exports" element={<AdminExportsPage />} />
          <Route path="/admin/decision-engine" element={<AdminDecisionEnginePage />} />
          <Route path="/scorecard" element={<ScorecardPage />} />
          <Route path="/demo/sheets-export" element={<SheetsExportDemo />} />
          <Route path="/admin/campaigns" element={<AdminCampaignsPage />} />
          <Route path="/admin/assets" element={<AdminAssetsPage />} />
          <Route path="/admin/shiplog" element={<AdminShipLogPage />} />
          <Route
            path="/spray-today"
            element={
              <ProtectedRoute>
                <SprayTodayPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/nurture"
            element={
              <AdminRoute>
                <AdminNurturePage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/founders"
            element={
              <AdminRoute>
                <AdminFoundersPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/glow-up"
            element={
              <AdminRoute>
                <AdminGlowUpPage />
              </AdminRoute>
            }
          />
          {/* Friendly redirects — these aren't real routes, but people type
              them in the URL bar expecting them to work. Bounce to homepage
              with the auth modal pre-opened so they land where they meant. */}
          <Route path="/login" element={<Navigate to="/?auth=1" replace />} />
          <Route path="/signin" element={<Navigate to="/?auth=1" replace />} />
          <Route path="/sign-in" element={<Navigate to="/?auth=1" replace />} />
          <Route path="/register" element={<Navigate to="/?auth=1" replace />} />
          <Route path="/signup" element={<Navigate to="/?auth=1" replace />} />
          <Route path="/sign-up" element={<Navigate to="/?auth=1" replace />} />
          <Route
            path="/console"
            element={
              <AdminRoute>
                <ConsolePage />
              </AdminRoute>
            }
          />
          <Route path="/admin/blog/new" element={<BlogAdminEditorPage />} />
          <Route path="/admin/blog/:slug/edit" element={<BlogAdminEditorPage />} />
          <Route path="/try" element={<TryDiagnosisPage />} />
          <Route path="/try-diagnosis" element={<TryDiagnosisPage />} />
          <Route path="/compare/sunday-vs-turfpulse" element={<SundayComparePage />} />
          <Route path="/integrations/home-assistant" element={<HomeAssistantIntegrationPage />} />
          <Route path="/inner-circle/success" element={<InnerCircleSuccessPage />} />
          <Route
            path="/methodology/probe-driven-irrigation"
            element={<ProbeDrivenIrrigationPage />}
          />
          <Route path="/credits" element={<DataSourcesPage />} />
          <Route path="/data-sources" element={<DataSourcesPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/getting-started" element={<GettingStartedPage />} />
          <Route path="/start" element={<Navigate to="/getting-started" replace />} />
          <Route path="/ecowitt-to-rachio" element={<EcowittToRachioPage />} />
          <Route path="/help-me-connect-ecowitt" element={<HelpMeConnectEcowittPage />} />
          <Route path="/admin/journal" element={<AdminJournalPage />} />
          <Route path="/for-contractors" element={<ContractorsPage />} />
          <Route path="/rachio-advanced-settings-vs-soil-probes" element={<RachioCalibrationVsProbesPage />} />
          <Route path="/protect-your-probes" element={<ProtectYourProbesPage />} />
          <Route path="/round-rock-rebate" element={<RoundRockRebatePage />} />
          <Route path="/probe-protection" element={<Navigate to="/protect-your-probes" replace />} />
          <Route path="/probes-landscapers" element={<Navigate to="/protect-your-probes" replace />} />
          <Route path="/rachio-calibration" element={<Navigate to="/rachio-advanced-settings-vs-soil-probes" replace />} />
          <Route path="/calibration-vs-probes" element={<Navigate to="/rachio-advanced-settings-vs-soil-probes" replace />} />
          <Route path="/contractors" element={<Navigate to="/for-contractors" replace />} />
          <Route path="/irrigation-contractors" element={<Navigate to="/for-contractors" replace />} />
          <Route path="/help-me-connect-rachio" element={<Navigate to="/help-me-connect-ecowitt" replace />} />
          <Route path="/connect-ecowitt-to-rachio" element={<Navigate to="/ecowitt-to-rachio" replace />} />
          <Route path="/rachio-soil-sensor" element={<Navigate to="/ecowitt-to-rachio" replace />} />
          {/* Legacy redirects — these paths used to 404. Keep them mapped so
              any external link, old back-button, or widget upsell still works. */}
          <Route path="/dashboard" element={<Navigate to="/app" replace />} />
          <Route path="/pulse" element={<ProtectedRoute><TurfPulseDashboard /></ProtectedRoute>} />
          {/* Short URL: /p → /pulse. Easy to type on mobile, and a
              friendly target when someone shares the dashboard link. */}
          <Route path="/p" element={<Navigate to="/pulse" replace />} />
          {/* Keep the WelcomeHub reachable — it's the onboarding "first
              steps" surface, useful for new users and the in-app guide.
              Default root (/) sends logged-in users to /pulse now. */}
          <Route path="/welcome" element={<ProtectedRoute><WelcomeHubPage /></ProtectedRoute>} />
          <Route path="/turf-pulse" element={<ProtectedRoute><TurfPulseDashboard /></ProtectedRoute>} />
          <Route path="/pulse/moisture" element={<ProtectedRoute><MoisturePage /></ProtectedRoute>} />
          <Route path="/pulse/ec" element={<ProtectedRoute><ECPage /></ProtectedRoute>} />
          <Route path="/pulse/vpd" element={<ProtectedRoute><VPDPage /></ProtectedRoute>} />
          <Route path="/pulse/mow-window" element={<ProtectedRoute><MowWindowPage /></ProtectedRoute>} />
          <Route path="/pulse/windows" element={<ProtectedRoute><ApplicationWindowsPage /></ProtectedRoute>} />
          {/* Trends + Activity — destinations for the bottom-nav Trends
              and History (Clock) buttons. Both reuse the dark Pulse chrome. */}
          <Route path="/trends"   element={<ProtectedRoute><TrendsPage /></ProtectedRoute>} />
          <Route path="/activity" element={<ProtectedRoute><ActivityPage /></ProtectedRoute>} />
          <Route path="/fires"    element={<ProtectedRoute><RecentFiresPage /></ProtectedRoute>} />
          <Route path="/moisture-alerts" element={<ProtectedRoute><MoistureAlertRulesPage /></ProtectedRoute>} />
          <Route path="/calibration"     element={<ProtectedRoute><CalibrationReferencePage /></ProtectedRoute>} />
          <Route path="/alerts" element={<ProtectedRoute><AlertsPage /></ProtectedRoute>} />
          <Route path="/subscription" element={<Navigate to="/pricing" replace />} />
          <Route
            path="/lawn-care/:citySlug"
            element={<RegionalLawnCarePage />}
          />
          <Route
            path="/partners/contractor"
            element={<ContractorPartnerPage />}
          />
          <Route path="/widget" element={<WidgetPage />} />
          <Route
            path="/sensors/placement"
            element={
              <ProtectedRoute>
                <SensorPlacementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sensors/calibration"
            element={
              <ProtectedRoute>
                <SoilCalibrationWizard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sensors/calibration/:zoneId"
            element={
              <ProtectedRoute>
                <SoilCalibrationWizard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sensors/zone-profiles"
            element={
              <ProtectedRoute>
                <ZoneProfilesPage />
              </ProtectedRoute>
            }
          />
        </Routes>
        <SiteFooter />
        <CookieConsentBanner />
        <Toaster />
      </BrowserRouter>
    </AuthProvider>
    </ThemeProvider>
    </Suspense>
  );
}

const PaymentSuccessPage = () => {
  const [status, setStatus] = React.useState('checking');
  const [sessionId, setSessionId] = React.useState('');

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get('session_id');
    if (sid) {
      setSessionId(sid);
      pollPaymentStatus(sid, 0);
    }
  }, []);

  const pollPaymentStatus = async (sid, attempts) => {
    if (attempts >= 5) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await fetch(
        `${API_URL}/api/payments/status/${sid}`,
        { credentials: 'include' }
      );
      const data = await response.json();

      if (data.payment_status === 'paid') {
        setStatus('success');
      } else if (data.status === 'expired') {
        setStatus('expired');
      } else {
        setTimeout(() => pollPaymentStatus(sid, attempts + 1), 2000);
      }
    } catch (error) {
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-8">
      <div className="max-w-md w-full text-center">
        {status === 'checking' && (
          <div>
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <h2 className="text-2xl font-bold mb-2">Processing Payment...</h2>
            <p className="text-muted-foreground">Please wait while we confirm your payment.</p>
          </div>
        )}
        {status === 'success' && (
          <div>
            <div className="text-6xl mb-4">✅</div>
            <h2 className="text-2xl font-bold mb-2">Payment Successful!</h2>
            <p className="text-muted-foreground mb-6">
              Thank you for upgrading to Premium. You now have access to advanced features.
            </p>
            <a
              href="/app"
              className="inline-block bg-primary text-primary-foreground px-6 py-3 rounded-lg hover:bg-primary/90"
            >
              Go to Dashboard
            </a>
          </div>
        )}
        {status === 'error' && (
          <div>
            <div className="text-6xl mb-4">❌</div>
            <h2 className="text-2xl font-bold mb-2">Payment Error</h2>
            <p className="text-muted-foreground mb-6">
              There was an error processing your payment. Please try again or contact support.
            </p>
            <a
              href="/app"
              className="inline-block bg-primary text-primary-foreground px-6 py-3 rounded-lg hover:bg-primary/90"
            >
              Back to Dashboard
            </a>
          </div>
        )}
        {status === 'timeout' && (
          <div>
            <div className="text-6xl mb-4">⏱️</div>
            <h2 className="text-2xl font-bold mb-2">Status Check Timeout</h2>
            <p className="text-muted-foreground mb-6">
              Payment verification is taking longer than expected. Please check your email for confirmation.
            </p>
            <a
              href="/app"
              className="inline-block bg-primary text-primary-foreground px-6 py-3 rounded-lg hover:bg-primary/90"
            >
              Go to Dashboard
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default App;
